import { Fragment, useEffect, useRef } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { DayCard } from '../common/DayCard';
import { PopOverForTimeValidation } from '../common/PopOverForTimeValidation';
import { useAuth } from '../../context/AuthContext';
import { useLegacyDidUpdate, useLegacyState } from '../../hooks/useLegacyState';
import { deleteSelectedTaskData } from '../../services/apiClient';
import { getErrorMessage } from '../../utils/errorMessage';
import { blockInvalidChar } from '../../utils/validations';
import warningIcon from '../../logo/warning.png';
import type {
  ErrorState,
  FreezeDateData,
  PastWeekLeaveMismatches,
  ProjectListItem,
  ProjectNameAll,
  RightPanelDivClassNameProperty,
  SelectedDate,
  SelectedTaskValue,
  TaskData,
  TimeDateEntry,
  TimeSheet,
} from '../../types';

/**
 * Port of `menuComponents/timeEntryMainPage.jsx` — the week grid: total-hours header, the
 * project filter dropdown, the seven day cards, and one row per task with a time input per
 * day.
 *
 * ## What was dropped, and why it is safe
 *
 * Under this tsconfig (`noUnusedLocals`) a dead local function is a compile error, so the
 * following had to go. Every one of them was already unreachable:
 *
 * - `saveWipDoneChange` — its only call site is commented out (jsx:413-418). Dropping it
 *   also drops the `updateTaskDone` import, so no request disappears.
 * - `focusTextInput`, `handleTogle`, `toggleVisibility`, `updateSelectedProjectAllState`,
 *   `handleChange`, `handleSelectInput`, `onKeyUpFunction` — never called from anywhere.
 *   The last two were empty bodies.
 * - The `textInput` and `myRef` refs (only `dropdownRef` is attached to anything), the
 *   module-level `const target = HTMLInputElement`, the `const { ref } = this.state`
 *   destructure, and the `styleForTooltip` object whose only use is commented out.
 * - The `ALT_KEY_CODE`, `validation1`, `TaskDoneWarningModel` and `DropdownButton`
 *   imports — imported, never referenced. (`validation1` is not even defined in
 *   validations.js; it has always been `undefined`.)
 *
 * The **state shape is kept whole**, including the ten keys nothing ever reads
 * (`selectedOptions`, `isVisible`, `ref`, `toggleText`, `selectedProjetId`,
 * `selectedTaskData`, `isTaskDone`, `taskDoneIds`, `deleteProjectId`, `alertForTimeType`)
 * and the two that only ever appear in a `setState` (`loading`, `userTimeSheetData`).
 * Unused object properties cost nothing here, and keeping the `setState` calls that write
 * them preserves the exact re-render sequence.
 *
 * ## Preserved deliberately
 *
 * - `componentWillUnmount` calls `removeEventListener("click", …)` and then
 *   `addEventListener("focusout", …)` — the second is almost certainly a typo for
 *   `remove`, and it leaks a listener on every unmount. Reproduced as written.
 * - `alt` on `<div>`, `<tr>`, `<td>`, `<label>`, `<span>` and `<button>`, plus numeric
 *   `id` and `label`/`value` on `Dropdown.Item`: invalid HTML that React still renders.
 *   Passed through prop spreads so the DOM is unchanged.
 * - The `alt` on the hour count interpolates the *function* rather than calling it
 *   (jsx:874 — `${this.calTotalHoursForCurrentWeek}` with no parentheses), so the
 *   attribute contains the function's source text. Reproduced; see the call site.
 * - `<div>` elements inside `<tbody>` wrapping the `<tr>`s. Invalid table markup that the
 *   browser hoists, but `App.css` is byte-identical and the "notify" empty state is one of
 *   those divs.
 * - `class=` became `className=` throughout. React renders `class` either way; this only
 *   removes a console warning.
 * - `key={index}` was added to every mapped list. The original supplied no key, so React
 *   reconciled by position — `index` reproduces exactly that, and keys are not rendered.
 */

/** react-bootstrap's `onEntering`, which recolours the tooltip node in place. */
function entering(element: HTMLElement): void {
  const inner = element.children[1] as HTMLElement | undefined;
  if (!inner) return;
  inner.style.backgroundColor = '#2b7bcc';
  inner.style.color = '#fff';
  inner.style.borderColor = '#2b7bcc';
}

/**
 * `day.date <= freezeDateData?.configValue` as JavaScript performs it.
 *
 * The original compares a `string` against a possibly-`undefined` config value; JS coerces
 * `undefined` to NaN-ish and the comparison is false. TypeScript rejects the expression
 * outright, so the coercion is isolated here rather than "fixed" at the call sites.
 */
function lte(a: unknown, b: unknown): boolean {
  return (a as string) <= (b as string);
}

/**
 * Attributes React renders but the JSX types reject: a numeric `id`, and `alt`/`label`/
 * `value` on elements and components that declare no such prop.
 *
 * The original emits every one of them, and the rendered DOM is the contract here — the
 * verbatim `App.css` and the screen-reader `alt` text both depend on them — so they are
 * passed through rather than dropped. The cast is deliberate and confined to this helper;
 * React stringifies each value on its way to the attribute, which is exactly what the
 * untyped original produced.
 */
function passthroughAttrs(
  attrs: Record<string, string | number | undefined>,
): Record<string, string> {
  return attrs as Record<string, string>;
}

/** Every option row handed to `handleOptionToggle` — the "All" row or a real project. */
type ProjectToggleOption = ProjectNameAll | ProjectListItem;

interface TimeEntryMainPageState {
  error: ErrorState;
  selectedOptions: ProjectListItem[];
  isVisible: boolean;
  ref: null;
  toggleText: string;
  selectedProjectsNames: string;
  /**
   * `projectName_All.isSelected` drives the "All" checkbox.
   * `projectName_All.allSelected` distinguishes "the user just clicked All" from "the user
   * is toggling individual projects", which is what picks the branch in
   * `handleOptionToggle`.
   */
  projectName_All: ProjectNameAll;
  selectedProjetId: string;
  selectedTaskData: unknown;
  isTaskDone: boolean;
  taskDoneIds: unknown[];
  deleteProjectId: null;
  alertForTimeType: boolean;
  dropDownClassName: string;
  /** Written by the delete-task failure path; never read. */
  loading?: boolean;
  /** Written by `handleTaskAssignmentStatus`; never read (the real copy lives in mainMenu). */
  userTimeSheetData?: TimeSheet[];
}

export interface TimeEntryMainPageProps {
  selectedDate: SelectedDate;
  userTimeSheetData: TimeSheet[] | undefined;
  projectList: ProjectListItem[] | undefined;
  selectedTaskValue: SelectedTaskValue;
  freezeDateData: FreezeDateData | undefined;
  pastWeekLeaveMismatchesDetails: PastWeekLeaveMismatches | undefined;
  rightPanelDivClassNameProperty: RightPanelDivClassNameProperty;
  loggedUserGteEmployeeNo: number;
  operationalUserGteEmployeeNo: number;
  clickInput: boolean;
  loading: boolean;
  /** Passed by mainMenu (`!clickInput`) but never read here. Declared for the record. */
  isDisabled?: boolean;

  updateStateofIsTaskDone: (value: boolean) => void;
  updateStateOfUserTimeSheetData: (userTimeSheetData: TimeSheet[] | undefined) => void;
  updateStateOfProjectList: (projectList: ProjectListItem[] | undefined) => void;
  updateStateOfRightPanelDivClassNameProperty: (next: RightPanelDivClassNameProperty) => void;
  updateSelectedTaskValue: (selectedTaskValue: SelectedTaskValue) => void;
  refreshTimeSheetData: (selectedDate: SelectedDate, empNo: number, projId: number) => void;
  deleteTaskData: (selectedTaskValue: SelectedTaskValue, gteProjectId: number) => void;
  addTimeEntered: (
    value: string,
    gteProjectId: number,
    task: TaskData,
    timedate: TimeDateEntry | undefined,
  ) => void;
  validateTimeEntered: (
    value: string,
    gteProjectId: number,
    task: TaskData,
    timedate: TimeDateEntry | undefined,
  ) => void;
  setSelectedTaskPanelValue: (
    timeSheet: TimeSheet,
    gteProjectId: number,
    task: TaskData,
    timedate: TimeDateEntry | undefined,
  ) => void;
}

export function TimeEntryMainPage(props: TimeEntryMainPageProps) {
  // Only for the "Click To Apply Absence" href below, which jsx:849 hardcoded to the
  // production Fusion deep link. Same literal is seeded into every environment's config,
  // so the rendered link is unchanged today.
  const { config } = useAuth();

  const { state, stateRef, setState } = useLegacyState<TimeEntryMainPageState>(() => ({
    error: {
      status: false,
      message: '',
    },
    selectedOptions: [],
    isVisible: false,
    ref: null,
    toggleText: 'Select Project',

    selectedProjectsNames: 'All',
    projectName_All: {
      projectName: 'All',
      isSelected: true,
      allSelected: true,
    },
    selectedProjetId: '',
    selectedTaskData: [],
    isTaskDone: false,
    taskDoneIds: [],
    deleteProjectId: null,
    alertForTimeType: false,

    dropDownClassName: 'projectlist_dropdown_menu dropdown-menu show',
  }));

  const dropdownRef = useRef<HTMLDivElement>(null);

  /*
   * componentDidMount / componentWillUnmount.
   *
   * The handler is declared inside the effect so that add and remove see one identity, as
   * the class's bound method did. The `addEventListener("focusout", …)` in the teardown is
   * the original's — kept, see the file header.
   */
  useEffect(() => {
    const handleDocumentClick = (event: Event): void => {
      // Clicking anywhere outside the dropdown closes it.
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setState({
          dropDownClassName: 'projectlist_dropdown_menu dropdown-menu',
        });
      }
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('focusout', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.addEventListener('focusout', handleDocumentClick);
    };
  }, [setState]);

  /** componentDidUpdate — a week change resets the project filter to "All". */
  useLegacyDidUpdate<TimeEntryMainPageProps, TimeEntryMainPageState>(
    props,
    state,
    (prevProps) => {
      if (prevProps.selectedDate?.week !== props.selectedDate?.week) {
        setState({
          selectedProjectsNames: 'All',

          projectName_All: {
            projectName: 'All',
            isSelected: true,
            allSelected: true,
          },
        });
      }
    },
  );

  const updateSelectedProjectsNames = (name: string): void => {
    setState({
      selectedProjectsNames: name,
    });
  };

  const getSelectedProjectsName = (projectList: ProjectListItem[] | undefined): string => {
    let projectListName = '';
    let n = 0;
    projectList?.map((project) => {
      if (project?.isSelected) {
        n++;
        if (n === 1) {
          projectListName = projectListName + project?.name;
        } else {
          projectListName = projectListName + '; ' + project?.name;
        }
      }
    });
    return projectListName === '' ? 'Select Options' : projectListName;
  };

  const updateIsViewedStateofUserTimeSheet = (value: boolean): TimeSheet[] | undefined => {
    const userTimeSheetData = props.userTimeSheetData?.map((project) => {
      return {
        ...project,
        isViewed: value,
        viewOnLoad:
          (project?.taskData?.length as number) > 0
            ? true
            : project.gteProjectId === -1
              ? true
              : false,
      };
    });
    props.updateStateOfUserTimeSheetData(userTimeSheetData);
    return userTimeSheetData;
  };

  const updateIsSelectedStateOfProjectList = (value: boolean): ProjectListItem[] | undefined => {
    const projectList = props.projectList?.map((project) => {
      return {
        ...project,
        isSelected: value,
      };
    });
    props.updateStateOfProjectList(projectList);
    return projectList;
  };

  /*
   * `projectName_All` is mutated in place and then written back — the object identity in
   * state never changes. `useLegacyState` keeps object identity across commits, so a read
   * on the very next line sees the mutation exactly as `this.state` did.
   */
  const updateProjectName_AllState = (value: boolean): void => {
    const projectName_All = stateRef.current.projectName_All;
    projectName_All.isSelected = value;
    projectName_All.allSelected = true;
    setState({
      projectName_All: projectName_All,
    });
  };

  const setAllSelectedFalseOfProjectName_All = (): void => {
    const projectName_All = stateRef.current.projectName_All;
    projectName_All.allSelected = false;
    projectName_All.isSelected = false;
    setState({
      projectName_All: projectName_All,
    });
  };

  const handleSelectionOfCheckBoxOfAll = (isSelected: boolean): void => {
    if (isSelected) {
      updateSelectedProjectsNames('All');
    } else {
      // Both branches set "All" — the original's comment says otherwise, the code does not.
      updateSelectedProjectsNames('All');
    }
    // "View all" for both the all-selected and all-deselected cases.
    updateIsViewedStateofUserTimeSheet(true);
    updateIsSelectedStateOfProjectList(isSelected);
    updateProjectName_AllState(isSelected);
  };

  /*
   * These two return the updated array rather than relying on props, because a handler can
   * call them twice before the parent's state has committed. The original carries the same
   * note at jsx:299-303.
   */
  const updateSelectedProjectIsViewedStateofUserTimeSheet = (
    option: ProjectToggleOption,
    userTimeSheetData: TimeSheet[] | undefined,
  ): TimeSheet[] | undefined => {
    userTimeSheetData = userTimeSheetData?.map((project) => {
      if (project.gteProjectId === option.gteProjectId) {
        return {
          ...project,
          isViewed: !option.isSelected,
          viewOnLoad: !option.isSelected,
        };
      } else {
        return project;
      }
    });
    props.updateStateOfUserTimeSheetData(userTimeSheetData);
    return userTimeSheetData;
  };

  const updateSelectedProjectIsSelectedStateofProjectList = (
    option: ProjectToggleOption,
    projectList: ProjectListItem[] | undefined,
  ): ProjectListItem[] | undefined => {
    projectList = projectList?.map((project) => {
      if (project.gteProjectId === option.gteProjectId) {
        return { ...project, isSelected: !option.isSelected };
      } else {
        return project;
      }
    });
    props.updateStateOfProjectList(projectList);
    return projectList;
  };

  /** All checkboxes deselected one by one → fall back to the "view all" state. */
  const handleAllSelectedFalse_byUser = (userTimeSheetData: TimeSheet[] | undefined): void => {
    let isViewed = false;
    let isAllViewedTrue = true;
    userTimeSheetData?.map((timesheet) => {
      timesheet.isViewed && (isViewed = true);
      !timesheet.isViewed && (isAllViewedTrue = false);
    });
    if (!isViewed) {
      handleSelectionOfCheckBoxOfAll(false);
    }
    if (isAllViewedTrue) {
      setState({
        selectedProjectsNames: 'All',
      });
    }
  };

  const handleAllSelectedTrue_byUser = (userTimeSheetData: TimeSheet[] | undefined): void => {
    let isViewed = true;
    userTimeSheetData?.map((project) => {
      !project.isViewed && (isViewed = false);
    });
    if (isViewed) {
      handleSelectionOfCheckBoxOfAll(true);
    }
  };

  /**
   * First individual toggle after "All" was in effect: clear the blanket selection, then
   * apply this one option.
   */
  const handleAllselectedTrueCondition = (
    option: ProjectToggleOption,
    isSelected_All_Checkbox: unknown,
  ): void => {
    setAllSelectedFalseOfProjectName_All();
    let userTimeSheetData: TimeSheet[] | undefined;
    if (isSelected_All_Checkbox) {
      userTimeSheetData = props.userTimeSheetData;
    } else {
      userTimeSheetData = updateIsViewedStateofUserTimeSheet(false);
    }

    updateSelectedProjectIsViewedStateofUserTimeSheet(option, userTimeSheetData);
    const projectlist = updateSelectedProjectIsSelectedStateofProjectList(
      option,
      props.projectList,
    );
    const selectedProjectsNames = getSelectedProjectsName(projectlist);
    updateSelectedProjectsNames(selectedProjectsNames);
  };

  /** Dropdown checkbox selection handler. */
  const handleOptionToggle = (option: ProjectToggleOption): void => {
    setState({
      dropDownClassName: 'projectlist_dropdown_menu dropdown-menu show',
    });

    if (option.projectName === 'All') {
      handleSelectionOfCheckBoxOfAll(!option.isSelected);
    } else {
      if (stateRef.current.projectName_All?.allSelected) {
        handleAllselectedTrueCondition(option, stateRef.current.projectName_All?.isSelected);
      } else {
        /*
         * Both branches do the same four things — the only difference is that the
         * "select" branch names the projects last and the "deselect" branch names them
         * first. Kept apart because the ordering decides which `setState` wins.
         */
        if (!option.isSelected) {
          const userTimeSheetData = updateSelectedProjectIsViewedStateofUserTimeSheet(
            option,
            props.userTimeSheetData,
          );
          const projectList = updateSelectedProjectIsSelectedStateofProjectList(
            option,
            props.projectList,
          );
          const selectedProjectsNames = getSelectedProjectsName(projectList);
          updateSelectedProjectsNames(selectedProjectsNames);
          handleAllSelectedTrue_byUser(userTimeSheetData);
        } else {
          const userTimeSheetData = updateSelectedProjectIsViewedStateofUserTimeSheet(
            option,
            props.userTimeSheetData,
          );
          const projectList = updateSelectedProjectIsSelectedStateofProjectList(
            option,
            props.projectList,
          );
          handleAllSelectedFalse_byUser(userTimeSheetData);
          const selectedProjectsNames = getSelectedProjectsName(projectList);
          updateSelectedProjectsNames(selectedProjectsNames);
        }
      }
    }
  };

  const calTotalHoursForCurrentWeek = (): string => {
    let total = 0;
    props.userTimeSheetData?.map((project) => {
      if (project.isViewed) {
        project.taskData?.map((task) => {
          total = total + task.totalTimeForTask;
        });
      }
    });
    total = Math.round((total + Number.EPSILON) * 100) / 100;
    return total.toFixed(2);
  };

  const handleTaskAssignmentStatus = (
    e: React.ChangeEvent<HTMLSelectElement>,
    task: TaskData,
    gteProjectId: number,
  ): void => {
    const userTimeSheetData = props.userTimeSheetData?.map((project) => {
      if (project.gteProjectId === gteProjectId) {
        const taskData = project.taskData?.map((taskdata) => {
          if (taskdata.taskId === task.taskId) {
            setState({
              selectedTaskData: {
                ...taskdata,
                taskAssignmentStatus: e.target.value,
              },
            });

            if (e.target.value === 'Done') {
              props.updateStateofIsTaskDone(true);
            }
            return {
              ...taskdata,
              taskAssignmentStatus: e.target.value,
              isChanged: true,
              // Required for the WIP call in mainMenu's saveDraft.
              isChangedWIPStatus: true,
            };
          } else {
            return taskdata;
          }
        });

        return { ...project, taskData: taskData, isChanged: true };
      } else {
        return project;
      }
    });

    setState({
      userTimeSheetData: userTimeSheetData,
    });
    props.updateStateOfUserTimeSheetData(userTimeSheetData);
  };

  const deleteTaskData = (
    gteProjectId: number,
    value: unknown,
    selectedTaskValue: SelectedTaskValue,
  ): void => {
    deleteSelectedTaskData(value)
      .then(() => {
        // An empty setState — a forced re-render, as in the original.
        setState({});
        props.updateSelectedTaskValue(selectedTaskValue);
        props.refreshTimeSheetData(
          props.selectedDate,
          props.operationalUserGteEmployeeNo,
          gteProjectId,
        );
        props.deleteTaskData(selectedTaskValue, gteProjectId);
      })
      .catch((error: unknown) => {
        const message = getErrorMessage(error);
        setState({
          loading: false,
          error: {
            status: message?.status === 401 ? false : message?.status === 404 ? false : true,
            message: message,
          },
        });
      });
  };

  const handleTashButton = (task: TaskData, timeSheet: TimeSheet): void => {
    const value = {
      gteProjectId: timeSheet.gteProjectId,
      taskAssignmentId: task.taskAssignmentId,
      timeSheetRecordId: task.timeSheetRecordId,
      loggedUserGteEmployeeId: props.loggedUserGteEmployeeNo,
      operationalUserGteEmployeeId: props.operationalUserGteEmployeeNo,
    };
    const selectedTaskValue: SelectedTaskValue = {
      gteProjectId: 0,
      timeSheet: timeSheet,
      task: {},
      timedate: {},
      newTimetotal: 0,
    };

    deleteTaskData(timeSheet.gteProjectId, value, selectedTaskValue);
  };

  /**
   * Highlights the clicked time slot by setting `isSelected` on its `timeDate` entry and
   * clearing it on every sibling of the same task. Gated on `clickInput`.
   */
  const highlightSelectedInput = (
    value: boolean,
    gteProjectId: number,
    task: TaskData,
    timedate: TimeDateEntry | undefined,
  ): void => {
    if (props.clickInput) {
      const userTimeSheetData = props.userTimeSheetData?.map((project) => {
        if (project.gteProjectId === gteProjectId) {
          const taskData = project.taskData?.map((taskdata) => {
            if (taskdata?.taskAssignmentId === task?.taskAssignmentId) {
              const timeDate = taskdata?.timeDate?.map((timeentrydate) => {
                if (timeentrydate.date === timedate?.date) {
                  return {
                    ...timeentrydate,

                    isSelected: value,
                  };
                } else {
                  return { ...timeentrydate, isSelected: false };
                }
              });
              return {
                ...taskdata,
                timeDate: timeDate,
              };
            } else {
              return taskdata;
            }
          });

          return { ...project, taskData: taskData };
        } else {
          return project;
        }
      });

      props.updateStateOfUserTimeSheetData(userTimeSheetData);
    }
  };

  const showAddTaskPanel = (
    rightPanelDivClassNameProperty: RightPanelDivClassNameProperty,
  ): void => {
    // Mutated in place, then handed to the parent — mainMenu holds this same object.
    // Note the trailing space in the alerts button class; it is in the original.
    rightPanelDivClassNameProperty.alerts = {
      button: 'accordion-button collapsed ',
      div: 'accordion-collapse collapse',
    };
    rightPanelDivClassNameProperty.addTask = {
      button: 'accordion-button',
      div: 'accordion-collapse collapse show',
    };
    rightPanelDivClassNameProperty.selectedTask = {
      button: 'accordion-button collapsed',
      div: 'accordion-collapse collapse',
    };
    props.updateStateOfRightPanelDivClassNameProperty(rightPanelDivClassNameProperty);
  };

  const handleNonProjectAddTaskButton = (
    rightPanelDivClassNameProperty: RightPanelDivClassNameProperty,
  ): void => {
    showAddTaskPanel(rightPanelDivClassNameProperty);
  };

  /**
   * One time cell.
   *
   * Every read re-runs `timeDate.find(...)` rather than hoisting it into a variable. That
   * is how the original is written and it matters here: the callback refs below read the
   * *current* entry at ref-attach time, and hoisting would be a behaviour change only if
   * the array were mutated mid-render — but the duplication is also the only way to keep
   * this diffable against the original, so it is left alone.
   */
  const renderInputComponent = (timeSheet: TimeSheet, task: TaskData, day: { date: string }) => {
    return (
      <input
        tabIndex={0}
        title={`${
          task?.timeDate?.find(({ date }) => date === day?.date)?.totalTime
        } hours for the task of ${task?.taskName} on ${day?.date}`}
        className={`timeSlot ${
          !task?.timeDate?.find(({ date }) => date === day?.date)?.istimeTypeEnableProject
            ? task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                  .timeEntryPendingStatus === 'A' ||
              task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                .timeEntryPendingStatus === 'R'
              ? 'PS'
              : task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                  .timeEntryStatus
            : ''
        } ${
          !task?.timeDate?.find(({ date }) => date === day?.date)?.istimeTypeEnableProject &&
          task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezed === 'Y'
            ? lte(day.date, props.freezeDateData?.configValue) && props.freezeDateData?.isFreezed
              ? 'quater_freeze'
              : 'week_freeze'
            : ''
        } ${
          task?.istimeTypeEnableProject &&
          timeSheet?.gteProjectId === props.selectedTaskValue?.gteProjectId &&
          task?.taskId === props.selectedTaskValue?.task?.taskId &&
          day?.date === props.selectedTaskValue?.timedate?.date
            ? 'active'
            : ''
        }`}
        style={{}}
        value={task?.timeDate?.find(({ date }) => date === day?.date)?.totalTime}
        disabled={
          // A time-type-enabled project is edited from the right panel, never disabled here.
          !task?.timeDate?.find(({ date }) => date === day?.date)?.istimeTypeEnableProject &&
          (task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
            .timeEntryStatus === 'B' ||
          task?.timeDate?.find(({ date }) => date === day.date)?.timeEntry[0].timeEntryStatus ===
            'A' ||
          task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezed === 'Y'
            ? true
            : false)
        }
        readOnly={
          task?.timeDate?.find(({ date }) => date === day?.date)?.istimeTypeEnableProject
            ? true
            : false
        }
        onBlur={(event) =>
          props.validateTimeEntered(
            event.target.value,
            timeSheet.gteProjectId,
            task,
            task?.timeDate?.find(({ date }) => date === day?.date),
          )
        }
        onChange={(event) => {
          props.addTimeEntered(
            event.target.value,
            timeSheet.gteProjectId,
            task,
            task?.timeDate?.find(({ date }) => date === day?.date),
          );
        }}
        onKeyUp={(e) => {
          if (blockInvalidChar.includes(e.key)) {
            (e.target as HTMLInputElement).value = '';
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        onCopy={(e) => {
          e.preventDefault();
          return false;
        }}
        ref={(inputElement) => {
          if (inputElement) {
            if (!task?.timeDate?.find(({ date }) => date === day?.date)?.istimeTypeEnableProject) {
              if (task?.timeDate?.find(({ date }) => date === day?.date)?.timeError) {
                inputElement.focus();
                inputElement.select();
              }
              if (
                !task?.timeDate?.find(({ date }) => date === day?.date)?.timeError &&
                task?.timeDate?.find(({ date }) => date === day?.date)?.isSelected
              ) {
                inputElement.select();
              }
              if (task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0].focused) {
                inputElement.focus();
              }
            }
          }
        }}
        onClick={() => {
          highlightSelectedInput(
            true,

            timeSheet.gteProjectId,
            task,
            task?.timeDate?.find(({ date }) => date === day?.date),
          );
          props.setSelectedTaskPanelValue(
            timeSheet,
            timeSheet.gteProjectId,
            task,
            task?.timeDate?.find(({ date }) => date === day?.date),
          );
        }}
        {...passthroughAttrs({
          id: task?.timeDate?.find(({ date }) => date === day?.date)?.timeRecordId,
        })}
      />
    );
  };

  const renderStatusForTooltip = (task: TaskData, day: { date: string }) => {
    return (
      <>
        {task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezed === 'Y' && (
          <>
            <strong>Time Restriction:</strong>{' '}
            <span>
              {task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezed === 'Y' &&
              lte(day.date, props.freezeDateData?.configValue) &&
              props.freezeDateData?.isFreezed
                ? task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezedMessage !== null
                  ? task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezedMessage
                  : ' Quarter Time Frozen'
                : task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezedMessage !== null
                  ? task?.timeDate?.find(({ date }) => date === day?.date)?.isFreezedMessage
                  : ' Weekly Time Frozen'}
            </span>
            <br></br>
          </>
        )}
        {!(
          task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0].timeEntryStatus ===
            'S' && task?.timeDate?.find(({ date }) => date === day?.date)?.totalTime === 0
        ) && (
          <>
            <strong>Time Status: </strong>{' '}
            <span>
              {task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                .timeEntryPendingStatus === 'A' ||
              task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                .timeEntryPendingStatus === 'R'
                ? 'Time Under Review for Approval'
                : task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                      .timeEntryStatus === 'U'
                  ? 'Time Submitted for Approval'
                  : task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                        .timeEntryStatus === 'A'
                    ? 'Time Approved by Time Approver'
                    : task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                          .timeEntryStatus === 'R'
                      ? 'Time Rejected by Time Approver'
                      : task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                            .timeEntryStatus === 'B'
                        ? 'Cell Blocked due to the end of the Project/Allocation/Task or Task Assignment'
                        : task?.timeDate?.find(({ date }) => date === day?.date)?.timeEntry[0]
                            .timeEntryStatus === 'S' && 'Time Saved'}
            </span>
          </>
        )}
      </>
    );
  };

  const tooltip = (tooltipProps: Record<string, unknown>) => (
    <Tooltip id="tooltipForRightPanel" className="time_type_status_tooltip" {...tooltipProps}>
      USE RIGHT PANEL TO ENTER TIME
    </Tooltip>
  );

  return (
    <>
      <div
        className="ch-task-content"
        {...passthroughAttrs({
          alt: `This table contain all the task available for ${props.selectedDate.dateOfFirstDayofCurrentWeek} to
           ${props.selectedDate.dateOfLastDayofCurrentWeek}`,
        })}
      >
        {((props.freezeDateData?.isUpdated && props.freezeDateData?.isFreezed) ||
          props.pastWeekLeaveMismatchesDetails?.status === 'Fail') && (
          <div className="ch-comm-notification">
            {props.freezeDateData?.isUpdated && props.freezeDateData?.isFreezed && (
              <span tabIndex={0}>
                <span>
                  <img
                    src={warningIcon}
                    style={{
                      width: '20px',
                      height: '20px',
                      padding: '2px',
                      display: 'inline-block',
                      marginRight: '5px',
                      marginBottom: '5px',
                    }}
                    alt="Warning Icon"
                  />
                </span>
                All Time Sheets up to {props.freezeDateData?.configValue} are now permanently
                frozen and cannot be unfrozen using the above ‘Unfreeze My Week’ button. <br />
              </span>
            )}

            {props.pastWeekLeaveMismatchesDetails?.isUpdated &&
              props.pastWeekLeaveMismatchesDetails?.status === 'Fail' && (
                <span className="">
                  <img
                    src={warningIcon}
                    style={{
                      width: '20px',
                      height: '20px',
                      padding: '2px',
                      display: 'inline-block',
                      marginRight: '5px',
                      marginBottom: '5px',
                    }}
                    alt="Warning Icon"
                  />
                  <span>
                    {props.pastWeekLeaveMismatchesDetails?.message}
                    {props.pastWeekLeaveMismatchesDetails?.dates?.map((item) => item).join(', ')}.
                    {/*
                      The link appears only for this one exact message string — an equality
                      test against a sentence the BFF returns. Preserved verbatim.
                    */}
                    {props.pastWeekLeaveMismatchesDetails?.message &&
                      props.pastWeekLeaveMismatchesDetails?.message ===
                        "You have entered leave time in CheckIn and haven't applied for absence. Dates: " && (
                        <a
                          className="pastWkLeave-link"
                          target="_blank"
                          href={config?.oracleFusionAbsenceUrl ?? ''}
                        >
                          {' '}
                          Click To Apply Absence
                        </a>
                      )}
                  </span>
                </span>
              )}
          </div>
        )}

        <div className="ch-task-container">
          <div className="ch-row">
            <div className="ch-time-detail-col">
              <div className="total-hr">
                <label tabIndex={0} {...passthroughAttrs({ alt: 'My Total Hours For This Week' })}>
                  MY TOTAL HOURS FOR THIS WEEK
                </label>
                <div
                  tabIndex={0}
                  className="hour-count"
                  /*
                   * jsx:874 interpolates the function itself — no parentheses — so this
                   * attribute holds the function's source text rather than a number.
                   * Reproduced; the visible value below is the called version.
                   */
                  {...passthroughAttrs({ alt: `${calTotalHoursForCurrentWeek} hours` })}
                >
                  {' '}
                  {calTotalHoursForCurrentWeek()}
                </div>
                <span tabIndex={0} {...passthroughAttrs({ alt: `hours` })}>
                  Hours
                </span>
              </div>

              <div className="">
                <Dropdown
                  className="projetlist_dropdown"
                  ref={dropdownRef}
                  {...passthroughAttrs({
                    title: `selected ${state.selectedProjectsNames} project list`,
                    size: '',
                  })}
                >
                  <Dropdown.Toggle
                    variant=""
                    className="projectlist_toggle"
                    title={`selected ${state.selectedProjectsNames} project list`}
                  >
                    {state.selectedProjectsNames}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={state.dropDownClassName}>
                    <Dropdown.Item
                      eventKey={-1}
                      onClick={() => handleOptionToggle(state.projectName_All)}
                      className=""
                      title={`${state.projectName_All.isSelected ? 'select' : 'unselect'} ${
                        state.projectName_All.projectName
                      }`}
                      {...passthroughAttrs({ id: -1, value: '-1' })}
                    >
                      <Form.Check
                        type="checkbox"
                        label={state.projectName_All.projectName}
                        value={-1}
                        checked={state.projectName_All.isSelected}
                        disabled={true}
                        {...passthroughAttrs({ id: -1 })}
                      />
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    {props.projectList?.map((option, index) => (
                      <Dropdown.Item
                        key={index}
                        eventKey={option.gteProjectId}
                        onClick={() => handleOptionToggle(option)}
                        title={`${option?.isSelected ? 'select' : 'unselect'} ${option?.name}`}
                        {...passthroughAttrs({ id: option.gteProjectId, label: option.name })}
                      >
                        <Form.Check
                          type="checkbox"
                          label={option.name}
                          checked={option.isSelected}
                          onChange={() => handleOptionToggle(option)}
                          disabled={true}
                          {...passthroughAttrs({ id: option.gteProjectId })}
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            {props.selectedDate.days?.map((day, index) => (
              <div className="ch-col8" key={index}>
                <DayCard key={day.date} day={day} selectedDate={props.selectedDate} />
              </div>
            ))}
          </div>

          <div className="task-block" {...passthroughAttrs({ alt: 'user time sheet panel' })}>
            <div className="ch-row">
              <table className="ch-time-table" role="presentation">
                <tbody className="table_scroll_div">
                  {(props.userTimeSheetData?.length ?? 0) > 0 ? (
                    props.userTimeSheetData?.map(
                      (timeSheet, timeSheetIndex) =>
                        timeSheet?.viewOnLoad &&
                        timeSheet?.isViewed && (
                          <div
                            key={timeSheetIndex}
                            {...passthroughAttrs({ id: timeSheet?.gteProjectId })}
                          >
                            {(timeSheet?.gteProjectId === -1 ||
                              (timeSheet?.isViewed && timeSheet?.display)) && (
                              <tr
                                className="project-name"
                                {...passthroughAttrs({
                                  id: timeSheet?.gteProjectId,
                                  alt: `panel of ${timeSheet?.projectName}`,
                                })}
                              >
                                <td
                                  className=""
                                  tabIndex={0}
                                  data-toggle="tooltip"
                                  data-placement="left"
                                  title={timeSheet?.projectName}
                                  {...passthroughAttrs({
                                    id: timeSheet?.gteProjectId,
                                    alt: timeSheet?.projectName,
                                  })}
                                >
                                  {timeSheet?.projectName}

                                  {timeSheet.gteProjectId === -1 && (
                                    <button
                                      tabIndex={0}
                                      className="button btn-sm add-non-project"
                                      data-toggle="tooltip"
                                      data-placement="left"
                                      title="Open Add Task Accordion panel"
                                      onClick={() =>
                                        handleNonProjectAddTaskButton(
                                          props.rightPanelDivClassNameProperty,
                                        )
                                      }
                                      {...passthroughAttrs({
                                        alt: 'Open Add Task Accordion panel',
                                      })}
                                    >
                                      &#10010;
                                    </button>
                                  )}
                                </td>
                                <td className=""></td>
                                <td className=""></td>
                                <td className=""></td>
                                <td className=""></td>
                                <td className=""></td>
                                <td className=""></td>
                                <td className=""></td>
                                <td className=""></td>
                              </tr>
                            )}
                            {timeSheet?.taskData?.map(
                              (task, taskIndex) =>
                                !(task?.timeSheetRecordStatus === 'H') && (
                                  <tr
                                    key={taskIndex}
                                    className="task_data"
                                    {...passthroughAttrs({ id: task?.taskAssignmentId })}
                                  >
                                    <td className="">
                                      <tr>
                                        <td>
                                          {task.taskAssignmentSource !== 'SWIFT' ? (
                                            !task.isTimeEntryStatusAsA && (
                                              <div className="col-3">
                                                <button
                                                  tabIndex={0}
                                                  className="trash btn btn-sm"
                                                  title="delete task button"
                                                  onClick={() => handleTashButton(task, timeSheet)}
                                                ></button>
                                              </div>
                                            )
                                          ) : (
                                            <div
                                              className="col-3 dropdown"
                                              {...passthroughAttrs({
                                                alt: 'select options of work in progress or done ',
                                              })}
                                            >
                                              <select
                                                tabIndex={0}
                                                title={`${
                                                  task.taskAssignmentStatus === 'I'
                                                    ? 'Work In Progress'
                                                    : 'Done'
                                                }`}
                                                key="wip"
                                                disabled={
                                                  task?.taskAssignmentStatus === 'D' ? true : false
                                                }
                                                className="wip-dropdown"
                                                value={task?.taskAssignmentStatus}
                                                onChange={(e) =>
                                                  handleTaskAssignmentStatus(
                                                    e,
                                                    task,
                                                    timeSheet.gteProjectId,
                                                  )
                                                }
                                              >
                                                {/* Two options: the current status, then its opposite. */}
                                                <option defaultValue={task.taskAssignmentStatus}>
                                                  {task.taskAssignmentStatus === 'I'
                                                    ? 'WIP'
                                                    : 'Done'}
                                                </option>
                                                <option
                                                  key={
                                                    task.taskAssignmentStatus === 'I'
                                                      ? 'Done'
                                                      : 'WIP'
                                                  }
                                                  value={
                                                    task.taskAssignmentStatus === 'I'
                                                      ? 'Done'
                                                      : 'WIP'
                                                  }
                                                >
                                                  {task.taskAssignmentStatus === 'I'
                                                    ? 'Done'
                                                    : 'WIP'}
                                                </option>
                                              </select>
                                            </div>
                                          )}
                                        </td>
                                        <td tabIndex={0} className="task-name" title={task?.taskName}>
                                          <span>{task?.taskName}</span>
                                        </td>
                                      </tr>
                                    </td>

                                    {props.selectedDate.days?.map((day, dayIndex) => (
                                      <td
                                        key={dayIndex}
                                        className=""
                                        id={
                                          'timeRecId: ' +
                                          task?.timeDate?.find(({ date }) => date === day.date)
                                            ?.timeRecordId
                                        }
                                      >
                                        <Fragment>
                                          {task?.timeDate?.find(({ date }) => date === day?.date)
                                            ?.istimeTypeEnableProject ? (
                                            <span className="">
                                              <OverlayTrigger
                                                trigger={['hover', 'focus']}
                                                placement="top"
                                                overlay={tooltip}
                                                onEntering={(e) => {
                                                  entering(e);
                                                }}
                                                {...passthroughAttrs({
                                                  id:
                                                    'timeRecId: ' +
                                                    task?.timeDate?.find(
                                                      ({ date }) => date === day.date,
                                                    )?.timeRecordId,
                                                })}
                                              >
                                                {renderInputComponent(timeSheet, task, day)}
                                              </OverlayTrigger>
                                            </span>
                                          ) : (task?.timeDate?.find(
                                              ({ date }) => date === day?.date,
                                            )?.timeEntry[0]?.isChanged &&
                                              task?.timeDate?.find(({ date }) => date === day?.date)
                                                ?.timeEntry[0]?.initialTime !==
                                                task?.timeDate?.find(
                                                  ({ date }) => date === day?.date,
                                                )?.timeEntry[0]?.time) ||
                                            (task?.timeDate?.find(({ date }) => date === day?.date)
                                              ?.timeEntry[0].timeEntryStatus === 'S' &&
                                              task?.timeDate?.find(({ date }) => date === day?.date)
                                                ?.timeEntry[0]?.time === 0 &&
                                              !(
                                                task?.timeDate?.find(
                                                  ({ date }) => date === day?.date,
                                                )?.isFreezed === 'Y'
                                              )) ? (
                                            renderInputComponent(timeSheet, task, day)
                                          ) : (
                                            <span className="">
                                              <OverlayTrigger
                                                placement="top"
                                                trigger={['hover', 'focus']}
                                                overlay={
                                                  <Tooltip
                                                    id={`tooltip-${
                                                      task?.timeDate?.find(
                                                        ({ date }) => date === day.date,
                                                      )?.timeRecordId
                                                    }`}
                                                    className="time_status_tooltip"
                                                  >
                                                    {renderStatusForTooltip(task, day)}
                                                  </Tooltip>
                                                }
                                                onEntering={(e) => {
                                                  entering(e);
                                                }}
                                                {...passthroughAttrs({
                                                  id:
                                                    'timeRecId: ' +
                                                    task?.timeDate?.find(
                                                      ({ date }) => date === day.date,
                                                    )?.timeRecordId,
                                                })}
                                              >
                                                {renderInputComponent(timeSheet, task, day)}
                                              </OverlayTrigger>
                                            </span>
                                          )}

                                          {task?.timeDate?.find(({ date }) => date === day?.date)
                                            ?.timeError && (
                                            /*
                                             * The original passes an `alt` here; the
                                             * component takes no props and never rendered
                                             * it.
                                             */
                                            <PopOverForTimeValidation />
                                          )}
                                        </Fragment>
                                      </td>
                                    ))}

                                    <td
                                      className="ch-time-row-total"
                                      tabIndex={0}
                                      title={`${task?.totalTimeForTask} Hours`}
                                    >
                                      {Math.round((task?.totalTimeForTask + Number.EPSILON) * 100) /
                                        100}{' '}
                                      Hrs
                                    </td>
                                  </tr>
                                ),
                            )}
                          </div>
                        ),
                    )
                  ) : (
                    <div className="notify">{props.loading ? 'Loading..' : 'No Data to Display'}</div>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
